/**
 * What the player's movement broadcasts: footstep one-shots and the noise
 * entities hear.
 *
 * Steps are driven by distance travelled rather than by a timer, so cadence
 * follows actual speed for free and a player pinned against a wall makes no
 * sound. The interval is jittered by a deterministic hash of the step index so
 * a long walk does not turn into a metronome.
 *
 * Noise is the hound's only sense, so it has to be honest: it scales with how
 * fast the player is really moving, water adds a splash penalty, and crouching
 * drops it below the threshold entities investigate at. Crouch-walking should
 * genuinely work.
 */

import { TUNING } from '../core/config';
import { clamp01, damp, hash1 } from '../core/math';
import { Cell, type EventBus, type GameEvents, type GeneratedLevel, type GeneratorKind, type Vec2 } from '../core/types';

export type StepSurface = GameEvents['player:step']['surface'];

/** Dry floors sound like whatever the level is built from. */
const SURFACE_FOR_GENERATOR: Record<GeneratorKind, StepSurface> = {
  lobby: 'carpet',
  warehouse: 'concrete',
  pipes: 'concrete',
  poolrooms: 'tile',
  hub: 'carpet',
  funrooms: 'carpet',
  motion: 'carpet',
  // Grey office carpet, and a hotel is carpeted wall to wall including the
  // back-of-house runs.
  offices: 'carpet',
  hotel: 'carpet',
  // Dusty concrete on Level 3, bare concrete on Level 6, and asphalt on Level
  // 11 — which is nearer to concrete than to anything else here, and the city's
  // pavements are concrete anyway.
  station: 'concrete',
  lightsout: 'concrete',
  city: 'concrete',
};

/** Fractional spread on the step interval, either side of 1. */
const INTERVAL_JITTER = 0.09;

/** Below this the noise is not worth telling anyone about. */
const NOISE_EPSILON = 0.02;

/** How fast the broadcast noise level follows the movement, 1/seconds. */
const NOISE_RATE = 11;

/** Loudness of a landing, before the water bonus. */
const LAND_NOISE = 0.8;

export interface Footsteps {
  /** Current surface under the player, refreshed by `update`. */
  readonly surface: StepSurface;
  /** True while the player is standing in water. */
  readonly inWater: boolean;
  /** Smoothed 0..1 noise level to publish on `PlayerState.noise`. */
  readonly noise: number;

  setLevel(level: GeneratedLevel | null): void;

  /**
   * Advance the step accumulator and the noise model.
   * @returns true on the frame a footstep fired, so the camera can sync its arc.
   */
  update(
    dt: number,
    pos: Vec2,
    distance: number,
    speed: number,
    crouching: boolean,
    sprinting: boolean
  ): boolean;

  /**
   * Ground covered by something other than the controller's own movement, at a
   * loudness the mover has already decided.
   *
   * Only an immersive session covers ground this way: the player walks with
   * their own legs, or teleports. Both are real journeys and both should sound
   * like one, but neither goes through the speed model above — a hop covers its
   * whole distance in a single frame, so `speed` there is an artefact of the
   * frame length rather than a rate anything should be priced on. So this feeds
   * the step accumulator and nothing else, and the caller supplies the
   * loudness. `update` is still called every frame by the controller, so the
   * surface underfoot is current.
   *
   * @returns true on the frame a footstep fired.
   */
  external(pos: Vec2, distance: number, loudness: number, speed: number): boolean;

  /** A landing: one loud step regardless of distance travelled. */
  land(pos: Vec2, impactSpeed: number): void;

  /** Forget accumulated distance, e.g. on teleport. */
  reset(): void;
}

class Steps implements Footsteps {
  surface: StepSurface = 'carpet';
  inWater = false;
  noise = 0;

  private level: GeneratedLevel | null = null;
  private dry: StepSurface = 'carpet';
  private travelled = 0;
  private nextAt = TUNING.player.stepInterval;
  private index = 0;
  private seed = 0;

  constructor(private readonly events: EventBus) {}

  setLevel(level: GeneratedLevel | null): void {
    this.level = level;
    this.dry = level ? (SURFACE_FOR_GENERATOR[level.spec.generator] ?? 'carpet') : 'carpet';
    this.surface = this.dry;
    this.seed = level ? level.seed : 0;
    this.reset();
  }

  update(
    dt: number,
    pos: Vec2,
    distance: number,
    speed: number,
    crouching: boolean,
    sprinting: boolean
  ): boolean {
    const P = TUNING.player;
    const cell = this.cellAt(pos);
    this.inWater = cell === Cell.Water;
    this.surface = this.inWater ? 'water' : this.dry;

    // Reference speed for "moving flat out in this stance", so a player easing
    // forward on the stick is quieter than one at full tilt.
    const reference = crouching ? P.crouchSpeed : sprinting ? P.sprintSpeed : P.walkSpeed;
    const effort = clamp01(speed / Math.max(reference, 0.1));
    const base = crouching ? P.noiseCrouch : sprinting ? P.noiseSprint : P.noiseWalk;
    const target = effort < 0.05 ? 0 : (base + (this.inWater ? P.noiseWaterBonus : 0)) * effort;
    this.noise = damp(this.noise, target, NOISE_RATE, dt);

    this.travelled += distance;
    if (this.travelled < this.nextAt) return false;

    this.travelled -= this.nextAt;
    // Guard against a huge frame dumping several steps at once.
    if (this.travelled > this.nextAt) this.travelled = 0;
    this.advanceInterval();
    this.emitStep(pos, speed, Math.max(this.noise, target));
    return true;
  }

  external(pos: Vec2, distance: number, loudness: number, speed: number): boolean {
    if (!(distance > 0)) return false;
    this.travelled += distance;
    if (this.travelled < this.nextAt) return false;

    this.travelled -= this.nextAt;
    // One hop can be longer than one step interval. Dropping the surplus rather
    // than firing a burst of footfalls from a single instant, exactly as the
    // guard in `update` does for a long frame.
    if (this.travelled > this.nextAt) this.travelled = 0;
    this.advanceInterval();
    this.emitStep(pos, speed, loudness);
    return true;
  }

  land(pos: Vec2, impactSpeed: number): void {
    const loud = clamp01(LAND_NOISE * clamp01(impactSpeed / TUNING.player.jumpSpeed)) +
      (this.inWater ? TUNING.player.noiseWaterBonus : 0);
    this.noise = Math.max(this.noise, Math.min(loud, 1));
    this.travelled = 0;
    this.advanceInterval();
    this.emitStep(pos, impactSpeed, this.noise);
  }

  reset(): void {
    this.travelled = 0;
    this.noise = 0;
    this.index = 0;
    this.advanceInterval();
  }

  private advanceInterval(): void {
    this.index++;
    const j = hash1(this.index, this.seed) * 2 - 1;
    this.nextAt = TUNING.player.stepInterval * (1 + j * INTERVAL_JITTER);
  }

  /**
   * A fresh Vec2 per step rather than a shared scratch: listeners are entitled
   * to keep the position (an entity remembers where it heard something), and a
   * couple of small objects per second is not a hot path.
   */
  private emitStep(pos: Vec2, speed: number, loudness: number): void {
    const at: Vec2 = { x: pos.x, z: pos.z };
    this.events.emit('player:step', { surface: this.surface, speed, pos: at });
    if (loudness > NOISE_EPSILON) {
      this.events.emit('player:noise', { pos: at, loudness: Math.min(loudness, 1) });
    }
  }

  private cellAt(pos: Vec2): Cell {
    const grid = this.level?.grid;
    if (!grid) return Cell.Floor;
    return grid.at(Math.floor(pos.x / grid.cellSize), Math.floor(pos.z / grid.cellSize));
  }
}

export function makeFootsteps(events: EventBus): Footsteps {
  return new Steps(events);
}
